import { useFormikContext } from "formik";
import ToolTip from "components/design/toolTip";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Input from "components/DesignSystem/Input/Input";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { BaseFileInput } from "components/DesignSystem/BaseFileInput/BaseFileInput";
import { LocalFilePreview, Preview } from "components/PreviewModal";
import { InkleFileData } from "components/fileTax/FileCompleteDocuments";
import { Info } from "components/icons/Info";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import {
  BUSINESS_DESCRIPTION_MAX_CHARS,
  interpolateQuestionText,
  QuestionOption,
  QuestionTextContext,
  TaxSetupQuestion as TaxSetupQuestionType,
  TaxSetupValues,
} from "v2/constants/taxSetupQuestions";
import { useDeleteTaxFileMutation } from "v2/store/taxSetupApi";
import { useUsStateOptions } from "v2/utils/useUsStateOptions";
import { useCountryOptions } from "v2/utils/useCountryOptions";
import { useGroupId } from "v2/utils/useGroupId";
import { SubsidiaryEntityList } from "./SubsidiaryEntityList";

const GENERIC_ERROR = "Something went wrong";

type TaxSetupQuestionProps = {
  question: TaxSetupQuestionType;
  parentQuestion?: TaxSetupQuestionType;

  namePrefix?: string;

  textContext: QuestionTextContext;

  homeStateId?: string;

  isRequired?: boolean;

  onFileUpload?: (file: File) => void;

  entityId?: string;

  season?: number;

  onboardingEntities?: { entityId: string; name: string }[];
};

const toArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value;
  return [];
};

const asFile = (value: unknown): File | null => {
  if (typeof File !== "undefined" && value instanceof File) return value;
  return null;
};

const isRemoteFile = (value: unknown): value is InkleFileData =>
  typeof value === "object" &&
  value !== null &&
  "uuid" in value &&
  "name" in value;

export const TaxSetupQuestion = ({
  question,
  parentQuestion,
  namePrefix = "",
  textContext,
  homeStateId,
  isRequired = false,
  onFileUpload,
  entityId,
  season,
  onboardingEntities,
}: TaxSetupQuestionProps) => {
  const { values, setFieldValue } = useFormikContext<TaxSetupValues>();
  const groupId = useGroupId();
  const { successToast, alertToast } = useToast();

  const [deleteTaxFile, { isLoading: isDeletingFile }] =
    useDeleteTaxFileMutation();

  const usStateOptions = useUsStateOptions(homeStateId);
  const allUsStateOptions = useUsStateOptions();
  const countryOptions = useCountryOptions();

  const homeStateOption =
    allUsStateOptions.find((option) => option.value === homeStateId) ?? null;

  const questionTextContext = {
    ...textContext,
    homeState: textContext.homeState || homeStateOption?.label,
  };

  const label = interpolateQuestionText(question.label, questionTextContext);
  const tooltip = question.tooltip
    ? interpolateQuestionText(question.tooltip, questionTextContext)
    : undefined;

  const filePreview = useModal();

  const isMulti = question.type === "multi";
  const isFile = question.fieldType === "file";
  const isEntityList = question.fieldType === "entityList";
  const isText =
    question.fieldType === "text" || question.fieldType === "number";
  const isTextArea = question.fieldType === "textarea";

  const fieldName = `${namePrefix}${question.id}`;
  const currentValue = values[fieldName];

  const localFile = asFile(currentValue);
  const remoteFile = isRemoteFile(currentValue) ? currentValue : null;
  const hasFile = Boolean(localFile || remoteFile);

  const handleFilePreview = (): void => {
    if (hasFile) filePreview.open();
  };

  const handleFileDelete = async (): Promise<void> => {
    try {
      if (remoteFile && entityId && season !== undefined) {
        await deleteTaxFile({ groupId, entityId, season }).unwrap();
        successToast({ message: "File removed" });
      }

      setFieldValue(fieldName, "", false);
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        GENERIC_ERROR;
      alertToast({ message });
    }
  };

  const handleFileDrop = (files: File[]): void => {
    const [file] = files;
    if (file) onFileUpload?.(file);
  };

  const optionsBySource = (source?: string): QuestionOption[] | null => {
    if (source === "usStates") return usStateOptions;
    if (source === "countries") return countryOptions;
    return null;
  };

  // `usStates` leaves out the home state, so a question that allows it (e.g. the
  // states employees sit in) adds it back. Resolved per question so a child
  // reading `optionsFromParent` sees the same list its parent offered.
  const resolveOptions = (target: TaxSetupQuestionType): QuestionOption[] => {
    const sourced =
      optionsBySource(target.optionsSource) ?? target.options ?? [];

    if (!target.includeHomeState || !homeStateOption) return sourced;

    const hasHomeState = sourced.some(
      (option) => option.value === homeStateOption.value
    );
    if (hasHomeState) return sourced;

    return [homeStateOption, ...sourced];
  };

  let options: QuestionOption[] = resolveOptions(question);

  if (question.optionsFromParent && parentQuestion) {
    const parentSelected = toArray(values[`${namePrefix}${parentQuestion.id}`]);

    options = resolveOptions(parentQuestion).filter((option) =>
      parentSelected.includes(option.value)
    );
  }

  let selectValue: QuestionOption | QuestionOption[] | null = null;

  if (isMulti) {
    const selectedValues = toArray(currentValue);
    selectValue = options.filter((option) =>
      selectedValues.includes(option.value)
    );
  }

  if (!isMulti) {
    selectValue =
      options.find((option) => option.value === currentValue) ?? null;
  }

  let shouldShowFollowUp = false;

  if (question.followUp && isMulti) {
    shouldShowFollowUp = toArray(currentValue).length > 0;
  }

  if (question.followUp && !isMulti) {
    const triggers = question.followUp.triggerValues ?? [];
    shouldShowFollowUp =
      typeof currentValue === "string" && triggers.includes(currentValue);
  }

  const activeGroups = (question.followUpGroups ?? []).filter(
    (group) =>
      typeof currentValue === "string" &&
      group.triggerValues.includes(currentValue)
  );

  let fieldElement = (
    <Combobox
      withForm
      required={isRequired}
      isMulti={isMulti}
      name={fieldName}
      placeholder={question.placeholder}
      options={options}
      value={selectValue}
      isClearable={false}
      shouldValidateOnChange={false}
      menuPortalTarget={document.body}
    />
  );

  if (isText) {
    fieldElement = (
      <Input
        withForm
        required={isRequired}
        name={fieldName}
        placeholder={question.placeholder}
      />
    );
  }

  if (isTextArea) {
    fieldElement = (
      <TextArea
        block
        rows={1}
        name={fieldName}
        maxLength={BUSINESS_DESCRIPTION_MAX_CHARS}
        placeholder={question.placeholder}
      />
    );
  }

  if (isFile) {
    fieldElement = (
      <BaseFileInput
        withForm
        block
        hideError
        name={fieldName}
        onDrop={handleFileDrop}
        onFileClick={handleFilePreview}
        onDelete={() => void handleFileDelete()}
        isDeleting={isDeletingFile}
      />
    );
  }

  if (isEntityList) {
    fieldElement = (
      <SubsidiaryEntityList
        key={entityId}
        parentEntityId={entityId}
        initialEntities={onboardingEntities}
      />
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-6">
      <div className="t-flex t-flex-col t-gap-1">
        <span className="t-text-body-sm t-text-text-60">
          {label}
          {isRequired && <span className="t-text-red">*</span>}

          {tooltip && (
            <ToolTip text={tooltip} side="right">
              <span className="t-ml-1 t-inline-flex t-align-middle t-text-text-30">
                <Info />
              </span>
            </ToolTip>
          )}
        </span>

        {fieldElement}
      </div>

      {shouldShowFollowUp && question.followUp && (
        <TaxSetupQuestion
          question={question.followUp.question}
          parentQuestion={question}
          namePrefix={namePrefix}
          textContext={textContext}
          homeStateId={homeStateId}
          onFileUpload={onFileUpload}
          entityId={entityId}
          season={season}
          onboardingEntities={onboardingEntities}
        />
      )}

      {activeGroups.map((group) => (
        <div
          key={group.triggerValues.join(",")}
          className="t-flex t-flex-col t-gap-4"
        >
          {group.questions.map((groupQuestion) => (
            <TaxSetupQuestion
              key={groupQuestion.id}
              question={groupQuestion}
              namePrefix={namePrefix}
              textContext={textContext}
              homeStateId={homeStateId}
              onFileUpload={onFileUpload}
              entityId={entityId}
              season={season}
              onboardingEntities={onboardingEntities}
            />
          ))}
        </div>
      ))}

      {filePreview.isOpen && localFile && (
        <LocalFilePreview isOpen file={localFile} close={filePreview.close} />
      )}

      {filePreview.isOpen && remoteFile && (
        <Preview
          showModal
          previewId={remoteFile.uuid}
          closeModal={filePreview.close}
        />
      )}
    </div>
  );
};
